import React, {
  createContext,
  useContext,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FunctionField,
  List,
  NumberField,
  TextField as RaTextField,
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  ReceiptLongOutlined as TransactionsIcon,
  GroupOutlined as AccountsIcon,
  AddCircleOutline as TopUpIcon,
  TuneOutlined as AdjustIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  DeleteOutline as DeleteIcon,
  PriorityHighRounded as WarningIcon,
} from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  FILTER_HEIGHT,
  ListActionsBar,
} from "@/components/common";
import { useCanCreate, useCanEdit, useCanDelete } from "@/utils/permissions";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  blockBalance as blockBalanceApi,
  unblockBalance as unblockBalanceApi,
} from "@/features/credit-balance/services/creditBalanceService";
import CreditPurchaseDialog, {
  type DialogMode,
} from "@/features/credit-balance/components/CreditPurchaseDialog";
import type { CostCenterRecord } from "../types/costCenter";
import CostCenterFormDialog from "./CostCenterFormDialog";
import CostCenterAccountsDialog from "./CostCenterAccountsDialog";

const i18nBase = "resources.credit-consumption/cost-center";
const RESOURCE = "credit-consumption/cost-center";

interface RowActionContextValue {
  openEdit: (record: CostCenterRecord) => void;
  openCreditTransactions: (record: CostCenterRecord) => void;
  openAccounts: (record: CostCenterRecord) => void;
  openTopUp: (record: CostCenterRecord) => void;
  openAdjust: (record: CostCenterRecord) => void;
  toggleBlocked: (record: CostCenterRecord) => Promise<void>;
  requestDelete: (record: CostCenterRecord) => void;
  canEdit: boolean;
  canDelete: boolean;
}

const RowActionContext = createContext<RowActionContextValue | null>(null);
const useRowActions = (): RowActionContextValue => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action used outside CostCenterList");
  }
  return ctx;
};

const BlockedBadge: React.FC = () => {
  const record = useRecordContext<CostCenterRecord>();
  const translate = useTranslate();
  if (!record) return null;
  return record.blocked ? (
    <Chip
      label={translate(`${i18nBase}.status.blocked`)}
      color="error"
      size="small"
    />
  ) : (
    <Chip
      label={translate(`${i18nBase}.status.active`)}
      color="success"
      size="small"
    />
  );
};

const RowMenu: React.FC = () => {
  const record = useRecordContext<CostCenterRecord>();
  const translate = useTranslate();
  const {
    openEdit,
    openCreditTransactions,
    openAccounts,
    openTopUp,
    openAdjust,
    toggleBlocked,
    requestDelete,
    canEdit,
    canDelete,
  } = useRowActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  if (!record) return null;
  const isBlocked = Boolean(record.blocked);
  const close = () => setAnchorEl(null);
  const item = (
    icon: React.ReactNode,
    labelKey: string,
    onClick: () => void,
    danger = false,
  ) => (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        close();
        onClick();
      }}
      sx={danger ? { color: "warning.main" } : undefined}
    >
      <ListItemIcon sx={{ color: "inherit" }}>{icon}</ListItemIcon>
      <ListItemText>{translate(labelKey)}</ListItemText>
    </MenuItem>
  );
  return (
    <>
      <IconButton
        size="small"
        aria-label="row actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
      >
        {canEdit &&
          item(
            <EditIcon sx={{ fontSize: 18 }} />,
            "ra.action.edit",
            () => openEdit(record),
          )}
        {item(
          <TransactionsIcon sx={{ fontSize: 18 }} />,
          `${i18nBase}.actions.creditTransactions`,
          () => openCreditTransactions(record),
        )}
        {item(
          <AccountsIcon sx={{ fontSize: 18 }} />,
          `${i18nBase}.actions.viewAccounts`,
          () => openAccounts(record),
        )}
        {item(
          <TopUpIcon sx={{ fontSize: 18 }} />,
          `${i18nBase}.actions.topUp`,
          () => openTopUp(record),
        )}
        {item(
          <AdjustIcon sx={{ fontSize: 18 }} />,
          `${i18nBase}.actions.adjust`,
          () => openAdjust(record),
        )}
        {isBlocked
          ? item(
              <LockOpenIcon sx={{ fontSize: 18 }} />,
              `${i18nBase}.actions.unblock`,
              () => void toggleBlocked(record),
            )
          : item(
              <LockIcon sx={{ fontSize: 18 }} />,
              `${i18nBase}.actions.block`,
              () => void toggleBlocked(record),
            )}
        {canDelete &&
          item(
            <DeleteIcon sx={{ fontSize: 18 }} />,
            `${i18nBase}.actions.delete`,
            () => requestDelete(record),
            true,
          )}
      </Menu>
    </>
  );
};

interface ListActionsProps {
  onAdd: () => void;
}

const ListActions: React.FC<ListActionsProps> = ({ onAdd }) => {
  const translate = useTranslate();
  const canCreate = useCanCreate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="costCenterName"
            label={translate(`${i18nBase}.filters.costCenterName`)}
          />
        </>
      }
      addButton={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
            startIcon={<AddIcon />}
            aria-label={translate(`${i18nBase}.actions.addCostCenter`)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              height: FILTER_HEIGHT,
              minWidth: { xs: FILTER_HEIGHT, sm: "auto" },
              borderRadius: 1.5,
              px: { xs: 0, sm: 2.5 },
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
                ml: { xs: 0, sm: -0.5 },
              },
              "&:hover": { boxShadow: "0 2px 6px rgba(11, 165, 183, 0.25)" },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {translate(`${i18nBase}.actions.addCostCenter`)}
            </Box>
          </Button>
        ) : null
      }
    />
  );
};

export const CostCenterList: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [accountsRecord, setAccountsRecord] =
    useState<CostCenterRecord | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<CostCenterRecord | null>(
    null,
  );
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();
  const [deleteOne, { isLoading: isDeleting }] = useDelete();

  const handlers: RowActionContextValue = {
    canEdit,
    canDelete,
    openEdit: (record) => setEditingId(record.id),
    openCreditTransactions: (record) =>
      navigate({
        pathname: "/credit-consumption/credit-transaction",
        search: `?costCenterName=${encodeURIComponent(record.name)}`,
      }),
    openAccounts: (record) => setAccountsRecord(record),
    openTopUp: (record) =>
      setDialogMode({
        kind: "topup",
        costCenterId: String(record.id),
        costCenterName: record.name,
      }),
    openAdjust: (record) =>
      setDialogMode({
        kind: "adjust",
        costCenterId: String(record.id),
        costCenterName: record.name,
      }),
    toggleBlocked: async (record) => {
      const action = record.blocked ? unblockBalanceApi : blockBalanceApi;
      const successKey = record.blocked
        ? `${i18nBase}.notificationsExtra.unblockSucceeded`
        : `${i18nBase}.notificationsExtra.blockSucceeded`;
      const failKey = record.blocked
        ? `${i18nBase}.notificationsExtra.unblockFailed`
        : `${i18nBase}.notificationsExtra.blockFailed`;
      try {
        await action(String(record.id));
        notify(successKey, { type: "success" });
        refresh();
      } catch (err: unknown) {
        notify(extractErrorMessage(err, failKey, translate), { type: "error" });
      }
    },
    requestDelete: (record) => setDeleteRecord(record),
  };

  const confirmDelete = (): void => {
    if (!deleteRecord) return;
    deleteOne(
      RESOURCE,
      { id: deleteRecord.id, previousData: deleteRecord },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(`${i18nBase}.notifications.deleted`, {
            type: "success",
            messageArgs: { name: deleteRecord.name, smart_count: 1 },
          });
          setDeleteRecord(null);
          refresh();
        },
        onError: (err: unknown) => {
          notify(
            extractErrorMessage(err, "ra.notification.http_error", translate),
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <List
          actions={<ListActions onAdd={() => setCreateOpen(true)} />}
          storeKey={false}
          disableSyncWithLocation
          sort={{ field: "0", order: "ASC" }}
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: 2,
              px: 0,
              py: { xs: 0.5, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="name"
            bulkActionButtons={false}
            disableMobileView
          >
            <FunctionField
              label={translate(`${i18nBase}.columns.actions`)}
              render={() => <RowMenu />}
            />
            <RaTextField source="name" />
            <RaTextField source="description" />
            <NumberField source="availableCredits" />
            <NumberField source="reservedCredits" />
            <NumberField source="unpaidCreditLimit" />
            <FunctionField
              source="blocked"
              label={translate(`${i18nBase}.columns.status`)}
              render={() => <BlockedBadge />}
            />
          </CustomDatagrid>
        </List>

        <CostCenterFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          recordId={null}
        />
        <CostCenterFormDialog
          open={editingId !== null}
          onClose={() => setEditingId(null)}
          recordId={editingId}
        />
        <CreditPurchaseDialog
          mode={dialogMode}
          onClose={() => setDialogMode(null)}
          onSuccess={() => {
            setDialogMode(null);
            refresh();
          }}
        />
        <CostCenterAccountsDialog
          record={accountsRecord}
          onClose={() => setAccountsRecord(null)}
        />

        <Dialog
          open={deleteRecord !== null}
          onClose={() => !isDeleting && setDeleteRecord(null)}
          PaperProps={{
            sx: { width: 380, maxWidth: "90vw", borderRadius: 2, p: 1 },
          }}
        >
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 4,
              pb: 1,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                border: "4px solid",
                borderColor: "warning.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <WarningIcon sx={{ fontSize: 40, color: "warning.main" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, textAlign: "center", mb: 1 }}
            >
              {deleteRecord
                ? translate(`${i18nBase}.delete.title`, {
                    recordName: deleteRecord.name,
                  })
                : ""}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              {translate(`${i18nBase}.delete.content`)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1 }}>
            <Button
              onClick={confirmDelete}
              disabled={isDeleting}
              variant="contained"
              sx={(t) => ({
                textTransform: "none",
                fontWeight: 600,
                minWidth: 100,
                height: 40,
                backgroundColor: `${t.palette.error.main} !important`,
                color: `${t.palette.common.white} !important`,
                boxShadow: "none !important",
                "&:hover": {
                  backgroundColor: `${t.palette.error.dark} !important`,
                  boxShadow: "none !important",
                },
                "&.Mui-disabled": {
                  backgroundColor: `${t.palette.error.light} !important`,
                  color: `${t.palette.common.white} !important`,
                  opacity: 0.6,
                },
              })}
            >
              {translate("ra.action.delete")}
            </Button>
            <Button
              onClick={() => setDeleteRecord(null)}
              disabled={isDeleting}
              variant="contained"
              sx={(t) => ({
                textTransform: "none",
                fontWeight: 600,
                minWidth: 100,
                height: 40,
                backgroundColor: `${t.palette.grey[200]} !important`,
                color: `${t.palette.text.primary} !important`,
                boxShadow: "none !important",
                "&:hover": {
                  backgroundColor: `${t.palette.grey[300]} !important`,
                  boxShadow: "none !important",
                },
              })}
            >
              {translate("ra.action.cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RowActionContext.Provider>
  );
};

export default CostCenterList;
